import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { waLink } from "@/lib/content";
import { Reveal, Overline } from "@/components/site/Reveal";
import { TourCard } from "@/components/site/TourCard";
import { CruiseSection } from "@/components/site/MiniCruises";
import { Contact } from "@/components/site/Contact";

export default function BoatPage() {
  const { slug } = useParams();
  const { t } = useLang();
  const boat = t.fleet.boats.find((b) => b.slug === slug);

  useEffect(() => {
    if (window.lenis) window.lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [slug]);

  if (!boat) return <Navigate to="/" replace />;

  return (
    <main data-testid="boat-page">
      {/* Banner */}
      <section className="relative h-[78svh] w-full overflow-hidden bg-navy" data-testid="boat-banner">
        <img src={boat.img} alt={boat.name} fetchpriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/10 to-navy/40" />

        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-start px-5 md:px-10 pt-28 md:pt-32">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link
              to="/"
              data-testid="boat-back-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-canvas/90 transition-colors hover:text-canvas"
            >
              ← {t.boatPage.back}
            </Link>
            <p className="mt-6 text-xs uppercase tracking-[0.28em] font-semibold text-canvas/80">{boat.tag}</p>
            <h1 className="mt-3 font-serif font-medium text-canvas text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight drop-shadow-[0_2px_14px_rgba(10,25,47,0.5)]">
              {boat.name}
            </h1>
            <p className="mt-5 max-w-xl font-medium text-base md:text-lg leading-relaxed text-canvas drop-shadow-[0_1px_10px_rgba(10,25,47,0.55)]">{boat.desc}</p>
          </motion.div>
        </div>

        <motion.a
          href={waLink(`${t.waMsg} (${boat.name})`)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="boat-banner-book"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="group absolute bottom-8 right-5 md:right-10 z-20 inline-flex items-center gap-3 rounded-full border border-[#C9A24B] bg-navy/40 px-6 py-3.5 text-[#E7C877] backdrop-blur-md shadow-[0_10px_34px_-10px_rgba(0,0,0,0.7)] transition-colors duration-300 hover:bg-[#C9A24B] hover:text-navy"
        >
          <span className="text-xs uppercase tracking-[0.18em] font-semibold">{t.boatPage.book}</span>
          <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </motion.a>
      </section>

      {/* Specs + features + gallery */}
      <section className="bg-canvas py-24 md:py-32" data-testid="boat-details">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <Overline>{t.boatPage.specsTitle}</Overline>
                <dl className="mt-6 grid grid-cols-2 gap-px border border-navy/10 bg-navy/10">
                  {boat.specs.map((s) => (
                    <div key={s.k} className="bg-canvas p-5">
                      <dt className="text-[10px] uppercase tracking-[0.15em] text-navy/45">{s.k}</dt>
                      <dd className="mt-1 font-serif text-xl text-navy">{s.v}</dd>
                    </div>
                  ))}
                </dl>
                <ul className="mt-8 space-y-3">
                  {boat.features.map((f) => (
                    <li key={f} className="flex gap-3 text-sm font-light text-navy/75">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-teak" />
                      {f}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-4">
                {(boat.gallery || []).map((g, i) => (
                  <Reveal
                    key={g + i}
                    delay={(i % 4) * 0.06}
                    className={`overflow-hidden border border-navy/10 ${i % 3 === 0 ? "col-span-2" : ""}`}
                  >
                    <img
                      src={g}
                      alt={`${boat.name} ${i + 1}`}
                      loading="lazy"
                      className={`w-full object-cover ${i % 3 === 0 ? "aspect-[16/9]" : "aspect-square"}`}
                    />
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Excursions */}
      <section id="excursions" className="bg-sand py-24 md:py-32" data-testid="boat-excursions">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal className="max-w-3xl mb-14 md:mb-16">
            <Overline>{t.boatPage.aboard} {boat.name}</Overline>
            <h2 className="mt-4 font-serif font-light text-navy text-4xl md:text-6xl tracking-tight">
              {t.boatPage.excursionsTitle}
            </h2>
            <p className="mt-6 font-light leading-relaxed text-navy/70 max-w-xl">{t.boatPage.excursionsSub}</p>
          </Reveal>

          <div className="grid gap-8 md:gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {boat.tours.map((tour, i) => (
              <TourCard key={tour.name} tour={tour} index={i} featuredLabel={t.boatPage.featuredLabel} />
            ))}
          </div>
        </div>
      </section>

      {boat.cruises && <CruiseSection cruises={boat.cruises} />}

      <Contact />
    </main>
  );
}
